// src/features/system/iam-admin/components/IamAdminPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Top-level zero-prop composer for the IAM Admin membership surface.
//
// Owns all state via the useIamAdmin hooks. Renders three regions:
//   • Left  → UserPickerCard
//   • Right → UserMembershipsCard
//   • Modal → AssignMembershipDialog
//
// Capability gate (canManageIam) is enforced at the parent (IAMManagement);
// this component assumes it is only mounted when the user is authorised.
// ────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import { TEXT } from '@/shared/dsp/cardTokens';
import UserPickerCard from './UserPickerCard';
import UserMembershipsCard from './UserMembershipsCard';
import AssignMembershipDialog from './AssignMembershipDialog';
import {
  useIamUsers,
  useIamTenants,
  useUserMemberships,
  useAssignMembership,
  useUpdateMembership,
  useRetireMembership,
} from '../hooks/useIamAdmin';
import type { Membership, MembershipStatus } from '../api/iamAdminApi';

export default function IamAdminPanel() {
  const [selectedUserRef, setSelectedUserRef] = useState<string | null>(null);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [mutatingRef, setMutatingRef] = useState<string | null>(null);

  const users  = useIamUsers();
  const tenants = useIamTenants();
  const memberships = useUserMemberships(selectedUserRef, includeRetired);

  const assign = useAssignMembership(selectedUserRef);
  const update = useUpdateMembership();
  const retire = useRetireMembership();

  const selectedUser = users.data?.find(u => u.userRef === selectedUserRef) ?? null;

  async function runMutation<T>(membershipRef: string, run: () => Promise<T>): Promise<void> {
    setMutatingRef(membershipRef);
    try { await run(); }
    finally { setMutatingRef(null); }
  }

  function handleChangeStatus(m: Membership, status: MembershipStatus) {
    if (status === m.status) return;
    void runMutation(m.membershipRef, () =>
      update.mutateAsync({ membershipRef: m.membershipRef, input: { status } }));
  }

  function handleToggleDefault(m: Membership, next: boolean) {
    if (next === m.isDefaultBusiness) return;
    void runMutation(m.membershipRef, () =>
      update.mutateAsync({ membershipRef: m.membershipRef, input: { is_default_business: next } }));
  }

  function handleRetire(m: Membership) {
    if (m.isRetired) return;
    const ok = typeof window === 'undefined'
      ? true
      : window.confirm(`Retire membership #${m.idMembership} for tenant ${m.tenantName ?? m.tenantId}?`);
    if (!ok) return;
    void runMutation(m.membershipRef, () => retire.mutateAsync(m.membershipRef));
  }

  return (
    <div className="space-y-3">
      <div className={TEXT.secondary}>
        Assign, modify and retire user memberships across tenants. All actions
        are audited; system tenant cannot be marked as default business.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-4">
          <UserPickerCard
            users={users.data ?? []}
            selectedRef={selectedUserRef}
            onSelect={setSelectedUserRef}
            isLoading={users.isLoading}
            isError={users.isError}
            errorMessage={users.error?.message}
          />
        </div>

        <div className="md:col-span-8">
          <UserMembershipsCard
            username={selectedUser?.username ?? null}
            userRef={selectedUserRef}
            list={memberships.data}
            isLoading={memberships.isLoading}
            isFetching={memberships.isFetching}
            isStale={memberships.isStale}
            isError={memberships.isError}
            lastFetchedAt={memberships.dataUpdatedAt}
            errorMessage={memberships.error?.message}
            includeRetired={includeRetired}
            onToggleRetired={setIncludeRetired}
            onAssignClick={() => setAssignOpen(true)}
            onChangeStatus={handleChangeStatus}
            onToggleDefault={handleToggleDefault}
            onRetire={handleRetire}
            mutatingRef={mutatingRef}
            canAssign={selectedUserRef != null}
          />
        </div>
      </div>

      <AssignMembershipDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        tenants={tenants.data ?? []}
        isLoading={tenants.isLoading}
        errorMessage={tenants.error?.message}
        onSubmit={async (input) => {
          await assign.mutateAsync(input);
        }}
      />
    </div>
  );
}
